import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { setAppLoader } from '@/redux/common/CommonActions';
import {
  generateShipmentNoEffect,
  getTrailerDetailsEffect,
  getTrailerRequestByIDEffect,
  getUserRelationEffect,
} from '@/redux/Savers/SaversEffects';

import { INITIAL_TRAILER_DETAILS, TAB_KEYS } from '../constants';

const useFieldsData = (
  companyId,
  requestID,
  formPickupManagement,
  formDeliveryManagement,
  setPickupFormValues,
  setDeliveryFormValues,
  isMultiple,
  nonMultiLabel,
  setCheckOptions,
  setPickupVendorId,
  setDeliveryVendorId,
  activeTabName,
  setTrailerId,
  setProductType,
) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    branchKey: null,
    createdBy: null,
    poNumberValue: null,
    shipmentNumber: null,
    soNumberValue: null,
    unitSize: null,
  });

  const getUserRelations = async () => {
    dispatch(setAppLoader(true));
    try {
      const { data } = await getUserRelationEffect();
      if (data.length > 0) {
        setFormData((prev) => ({
          ...prev,
          branchKey: data[0].branchKey,
        }));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const fetchTrailerRequestData = async (requestId) => {
    if (!requestId || requestId === '0') {
      return;
    }

    dispatch(setAppLoader(true));

    try {
      const { data: requestData } = await getTrailerRequestByIDEffect(requestId);

      if (!requestData.length || !requestData[0]?.trailerRequestData.length) {
        return;
      }

      const trailer = requestData[0].trailerRequestData[0];
      setFormData((prev) => ({
        ...prev,
        createdBy: trailer.createdBy,
      }));
      const {
        trailerID,
        trailerNumber,
        fullName,
        rscm,
        scs,
        unitSize,
        productTypeDesc,
        deliveryProductTypeDesc,
        deliveryProductType,
        productType,
        supplierNumber,
        locationID,
        branchDesc,
      } = trailer;
      const commonFormValues = {
        selPartner: supplierNumber,
        txtRSCM: rscm,
        txtSCS: scs,
        txtUserName: fullName,
        UnitSize: unitSize,
      };
      const pickupFormValues = {
        Commodity: productTypeDesc,
        productType,
        selTrailer: trailerNumber,
        ...commonFormValues,
      };
      setProductType(productType);
      setTrailerId(trailerID);
      const deliveryFormValues = {
        Commodity: deliveryProductTypeDesc,
        productType: deliveryProductType,
        ...commonFormValues,
      };

      formPickupManagement.setFieldsValue(pickupFormValues);
      formDeliveryManagement.setFieldsValue(deliveryFormValues);
      setFormData((prev) => ({
        ...prev,
        unitSize,
      }));
      const { data: trailerDetails } = await getTrailerDetailsEffect(trailerID);
      let extraValues = {};
      if (trailerDetails.length > 0) {
        const { yardName, unitRentalCompany, rollDoor, vendorId, unitSizeID } = trailerDetails[0];
        if (!isMultiple) {
          setPickupVendorId(vendorId);
        } else {
          if (activeTabName === TAB_KEYS.PICKUP) {
            setPickupVendorId(vendorId);
          } else {
            setDeliveryVendorId(vendorId);
          }
        }
        extraValues = {
          PickupRentalCompany: unitRentalCompany && unitRentalCompany !== 'NULL' ? unitRentalCompany : null,
          selYardValue: yardName,
          ...(unitSizeID ? { UnitSize: unitSizeID } : {}),
        };
        setCheckOptions((prev) => ({
          ...prev,
          rollDoor,
        }));
      }
      const orgLocations =
        trailerDetails.length > 0
          ? trailerDetails.map((trailer) => ({
              orgBookOks: trailer.bookOks,
              orgBookWeight: trailer.book,
              orgETD: null,
              orgFurn: trailer.furn,
              orgLocationId: locationID,
              orgMiscOks: trailer.miscOKs,
              orgMiscWeight: trailer.misc,
              orgOLI: trailer.oli,
              orgShipLocation: branchDesc,
              orgSoftOks: trailer.softOks,
              orgSoftWeight: trailer.soft,
              orgSpotDate: moment(trailer.loadedDate ?? undefined),
            }))
          : INITIAL_TRAILER_DETAILS.orgLocations.map((trailer) => ({
              ...trailer,
              orgLocationId: locationID,
              orgShipLocation: branchDesc,
            }));

      const getDestLocations = (shouldNullifyConsignee) =>
        trailerDetails.length > 0
          ? trailerDetails.map((trailer) => ({
              destBookOks: trailer.bookOks,
              destBookWeight: trailer.book,
              destConsignee: shouldNullifyConsignee ? null : branchDesc,
              destETA: null,
              destFurn: trailer.furn,
              destLocationId: locationID,
              destMiscOks: trailer.miscOKs,
              destMiscWeight: trailer.misc,
              destOLI: trailer.oli,
              destSoftOks: trailer.softOks,
              destSoftWeight: trailer.soft,
            }))
          : INITIAL_TRAILER_DETAILS.destLocations.map((trailer) => ({
              ...trailer,
              destConsignee: shouldNullifyConsignee ? null : branchDesc,
              destLocationId: shouldNullifyConsignee ? null : locationID,
            }));

      const finalPickupValues = {
        ...INITIAL_TRAILER_DETAILS,
        ...pickupFormValues,
        ...extraValues,
        destLocations: getDestLocations(true),
        orgLocations,
      };

      const finalDeliveryValues = {
        ...INITIAL_TRAILER_DETAILS,
        ...deliveryFormValues,
        ...extraValues,
        destLocations: getDestLocations(false),
      };

      if (isMultiple) {
        formPickupManagement.setFieldsValue(finalPickupValues);
        formDeliveryManagement.setFieldsValue(finalDeliveryValues);
        setPickupFormValues(finalPickupValues);
        setDeliveryFormValues(finalDeliveryValues);
      } else {
        const label = nonMultiLabel?.toLowerCase() || '';
        if (label.includes('pickup')) {
          formPickupManagement.setFieldsValue(finalPickupValues);
          setPickupFormValues(finalPickupValues);
        } else if (label.includes('delivery')) {
          formPickupManagement.setFieldsValue(finalDeliveryValues);
          setPickupFormValues(finalDeliveryValues);
        }
      }
    } catch (error) {
      console.error('Error fetching trailer request data:', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const generateShipmentNumber = async () => {
    try {
      dispatch(setAppLoader(true));

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (userInfo) {
        const result = await generateShipmentNoEffect(userInfo.id);
        if (result?.data) {
          const shipmentNum = result.data.toString();

          setFormData((prev) => ({
            ...prev,
            poNumberValue: `PO${shipmentNum}`,
            shipmentNumber: shipmentNum,
            soNumberValue: `SO${shipmentNum}`,
          }));
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Fix this
  useEffect(() => {
    getUserRelations();
    fetchTrailerRequestData(requestID);
    generateShipmentNumber();
  }, [companyId]);

  return {
    branchKey: formData.branchKey,
    createdBy: formData.createdBy,
    poNumberValue: formData.poNumberValue,
    shipmentNumber: formData.shipmentNumber,
    soNumberValue: formData.soNumberValue,
    unitSize: formData.unitSize,
  };
};

export default useFieldsData;
